import type { MaterialId } from "../../types";

/**
 * What every surface in the 3D Render is made of. Pure data: the scene
 * builders tag geometry with a {@link MaterialSpec}; the engine's material
 * factory turns specs into GPU materials (shared per unique spec).
 */

/** Photo-scanned CC0 texture sets (see `scripts/textures3d/sources.json`). */
export type TextureSetId =
  | "fabric"
  | "boucle"
  | "velvet"
  | "leather"
  | "oak"
  | "walnut"
  | "marble"
  | "quartz"
  | "granite"
  | "quartzite"
  | "soapstone"
  | "butcher-block"
  | "concrete"
  | "terrazzo"
  | "tile"
  | "metal"
  | "rattan"
  | "wool-rug"
  | "ceramic"
  | "satin"
  | "leaves"
  | "floor-oak"
  | "floor-porcelain"
  | "floor-stone"
  | "plaster"
  | "painted-wood"
  | "floor-walnut"
  | "floor-whitewash"
  | "floor-herringbone"
  | "carpet"
  | "shiplap"
  | "brick"
  | "plaster-rough"
  | "wall-tile"
  | "ceiling-planks"
  | "deck"
  | "pavers"
  | "grass"
  | "gravel"
  | "masonry"
  | "stucco";

/** Room surfaces: chosen in the Finishes sheet or inferred (`scene/finishes.ts`). */
export const FINISH_SURFACES = [
  "floor-oak",
  "floor-walnut",
  "floor-whitewash",
  "floor-herringbone",
  "floor-porcelain",
  "floor-stone",
  "floor-terrazzo",
  "floor-concrete",
  "floor-carpet",
  "ground-deck",
  "ground-pavers",
  "ground-grass",
  "ground-gravel",
  "wall-paint",
  "wall-plaster",
  "wall-shiplap",
  "wall-slat",
  "wall-felt",
  "wall-panel",
  "wall-brick",
  "wall-brick-painted",
  "wall-tile",
  "ceiling-paint",
  "ceiling-planks",
  "ceiling-beam",
  "fence-wood",
  "hedge",
  "masonry",
  "masonry-painted",
  "stucco",
  "house-siding",
  "pergola-wood",
  "exterior-paint",
  "trim-paint",
  "door-paint",
] as const;

export type FinishSurface = (typeof FINISH_SURFACES)[number];

/** Fixed-purpose surfaces used by the builders. */
export type SpecialSurface =
  | "glass-clear"
  | "screen"
  | "bulb"
  | "lampshade"
  | "chrome"
  | "black-metal"
  | "brass"
  | "art-print"
  | "mirror"
  | "soil"
  | "stem"
  | "book"
  | "rubber";

export type SurfaceId = MaterialId | FinishSurface | SpecialSurface;

export interface SurfaceDef {
  /** Texture set; null renders untextured PBR. */
  set: TextureSetId | null;
  /** Base roughness (multiplies the ORM roughness when a set is present). */
  roughness: number;
  metalness: number;
  normalScale: number;
  /** Scales the tint-neutral detail contrast (1 = as built). */
  detailStrength?: number;
  /** `colored` sets: 0 keeps the scanned colors, 1 moves fully to the tint. */
  tintStrength?: number;
  /** Read a `colored` set's luminance as detail under the tint (painted brick). */
  paintOver?: boolean;
  sheen?: { intensity: number; roughness: number };
  clearcoat?: { intensity: number; roughness: number };
  /** Physical glass (High quality); lower tiers fall back to opacity. */
  transmission?: number;
  ior?: number;
  /** Grain / strips follow the part's long axis. */
  directional?: boolean;
  /** Alpha-tested cut-outs (leaf cards). */
  alphaTest?: number;
  envMapIntensity?: number;
}

export const SURFACES: Record<SurfaceId, SurfaceDef> = {
  // Upholstery
  fabric: { set: "fabric", roughness: 1, metalness: 0, normalScale: 0.9, sheen: { intensity: 0.55, roughness: 0.75 } },
  boucle: { set: "boucle", roughness: 1, metalness: 0, normalScale: 1.2, sheen: { intensity: 0.45, roughness: 0.85 } },
  velvet: { set: "velvet", roughness: 1, metalness: 0, normalScale: 0.5, sheen: { intensity: 1, roughness: 0.35 } },
  leather: { set: "leather", roughness: 0.75, metalness: 0, normalScale: 1, clearcoat: { intensity: 0.2, roughness: 0.45 } },
  // Wood
  "wood-light": { set: "oak", roughness: 0.85, metalness: 0, normalScale: 0.6, directional: true, clearcoat: { intensity: 0.3, roughness: 0.35 } },
  "wood-mid": { set: "oak", roughness: 0.85, metalness: 0, normalScale: 0.6, directional: true, clearcoat: { intensity: 0.3, roughness: 0.35 } },
  "wood-dark": { set: "walnut", roughness: 0.8, metalness: 0, normalScale: 0.6, directional: true, clearcoat: { intensity: 0.35, roughness: 0.3 } },
  "butcher-block": { set: "butcher-block", roughness: 0.8, metalness: 0, normalScale: 0.6, directional: true },
  rattan: { set: "rattan", roughness: 0.9, metalness: 0, normalScale: 1.2 },
  // Stone and counter surfaces
  marble: { set: "marble", roughness: 0.35, metalness: 0, normalScale: 0.4, clearcoat: { intensity: 0.6, roughness: 0.08 } },
  quartz: { set: "quartz", roughness: 0.3, metalness: 0, normalScale: 0.3, clearcoat: { intensity: 0.5, roughness: 0.1 } },
  granite: { set: "granite", roughness: 0.35, metalness: 0, normalScale: 0.4, clearcoat: { intensity: 0.5, roughness: 0.1 } },
  quartzite: { set: "quartzite", roughness: 0.4, metalness: 0, normalScale: 0.4, clearcoat: { intensity: 0.45, roughness: 0.12 } },
  soapstone: { set: "soapstone", roughness: 0.75, metalness: 0, normalScale: 0.5 },
  concrete: { set: "concrete", roughness: 1, metalness: 0, normalScale: 0.8 },
  terrazzo: { set: "terrazzo", roughness: 0.4, metalness: 0, normalScale: 0.4, tintStrength: 0.55, clearcoat: { intensity: 0.4, roughness: 0.12 } },
  laminate: { set: "quartz", roughness: 0.55, metalness: 0, normalScale: 0.2, detailStrength: 0.45 },
  tile: { set: "tile", roughness: 0.5, metalness: 0, normalScale: 0.8, clearcoat: { intensity: 0.4, roughness: 0.15 } },
  ceramic: { set: "ceramic", roughness: 0.35, metalness: 0, normalScale: 0.3, clearcoat: { intensity: 0.8, roughness: 0.06 } },
  // Others
  glass: { set: null, roughness: 0.04, metalness: 0, normalScale: 0, transmission: 1, ior: 1.5 },
  metal: { set: "metal", roughness: 0.7, metalness: 1, normalScale: 0.4, directional: true },
  "wool-rug": { set: "wool-rug", roughness: 1, metalness: 0, normalScale: 1.2, sheen: { intensity: 0.35, roughness: 0.9 } },
  plastic: { set: "satin", roughness: 0.45, metalness: 0, normalScale: 0.3, clearcoat: { intensity: 0.2, roughness: 0.3 } },
  foliage: { set: "leaves", roughness: 0.65, metalness: 0, normalScale: 1, tintStrength: 0.7, alphaTest: 0.45, sheen: { intensity: 0.25, roughness: 0.6 } },
  matte: { set: "satin", roughness: 0.85, metalness: 0, normalScale: 0.4, detailStrength: 0.5 },
  // Room finishes. Colored floors take the chosen color as a stain: the
  // swatch that equals the scan's mean shows the scan untouched.
  "floor-oak": { set: "floor-oak", roughness: 0.85, metalness: 0, normalScale: 0.7, tintStrength: 0.75, directional: true, clearcoat: { intensity: 0.25, roughness: 0.25 } },
  "floor-walnut": { set: "floor-walnut", roughness: 0.8, metalness: 0, normalScale: 0.7, tintStrength: 0.75, directional: true, clearcoat: { intensity: 0.3, roughness: 0.22 } },
  "floor-whitewash": { set: "floor-whitewash", roughness: 0.85, metalness: 0, normalScale: 0.6, tintStrength: 0.75, directional: true, clearcoat: { intensity: 0.2, roughness: 0.3 } },
  "floor-herringbone": { set: "floor-herringbone", roughness: 0.8, metalness: 0, normalScale: 0.7, tintStrength: 0.7, directional: true, clearcoat: { intensity: 0.3, roughness: 0.22 } },
  "floor-porcelain": { set: "floor-porcelain", roughness: 0.6, metalness: 0, normalScale: 0.6, tintStrength: 0.6, clearcoat: { intensity: 0.25, roughness: 0.15 } },
  "floor-stone": { set: "floor-stone", roughness: 0.55, metalness: 0, normalScale: 0.5, tintStrength: 0.6, clearcoat: { intensity: 0.3, roughness: 0.12 } },
  "floor-terrazzo": { set: "terrazzo", roughness: 0.4, metalness: 0, normalScale: 0.4, tintStrength: 0.55, clearcoat: { intensity: 0.4, roughness: 0.12 } },
  "floor-concrete": { set: "concrete", roughness: 0.8, metalness: 0, normalScale: 0.5, detailStrength: 0.8, clearcoat: { intensity: 0.15, roughness: 0.4 } },
  "floor-carpet": { set: "carpet", roughness: 1, metalness: 0, normalScale: 1, detailStrength: 0.9, sheen: { intensity: 0.4, roughness: 0.9 } },
  "ground-deck": { set: "deck", roughness: 0.85, metalness: 0, normalScale: 0.8, tintStrength: 0.7, directional: true },
  "ground-pavers": { set: "pavers", roughness: 0.95, metalness: 0, normalScale: 0.9, tintStrength: 0.6 },
  "ground-grass": { set: "grass", roughness: 1, metalness: 0, normalScale: 0.9, tintStrength: 0.6, sheen: { intensity: 0.3, roughness: 0.7 } },
  "ground-gravel": { set: "gravel", roughness: 1, metalness: 0, normalScale: 1, tintStrength: 0.6 },
  // Fresh interior paint: just a hint of roller texture, never the plaster's marks.
  "wall-paint": { set: "plaster", roughness: 0.9, metalness: 0, normalScale: 0.14, detailStrength: 0.14 },
  "wall-plaster": { set: "plaster-rough", roughness: 0.95, metalness: 0, normalScale: 0.7, detailStrength: 0.55 },
  "wall-shiplap": { set: "shiplap", roughness: 0.8, metalness: 0, normalScale: 0.8, detailStrength: 0.5 },
  "wall-slat": { set: "oak", roughness: 0.8, metalness: 0, normalScale: 0.6, directional: true, clearcoat: { intensity: 0.15, roughness: 0.4 } },
  "wall-felt": { set: "fabric", roughness: 1, metalness: 0, normalScale: 0.6, detailStrength: 0.5, sheen: { intensity: 0.3, roughness: 0.9 } },
  "wall-panel": { set: "painted-wood", roughness: 0.6, metalness: 0, normalScale: 0.3, detailStrength: 0.3, clearcoat: { intensity: 0.15, roughness: 0.35 } },
  "wall-brick": { set: "brick", roughness: 0.95, metalness: 0, normalScale: 1, tintStrength: 0 },
  "wall-brick-painted": { set: "brick", roughness: 0.85, metalness: 0, normalScale: 1, paintOver: true, detailStrength: 0.6 },
  "wall-tile": { set: "wall-tile", roughness: 0.3, metalness: 0, normalScale: 0.8, detailStrength: 0.7, clearcoat: { intensity: 0.6, roughness: 0.08 } },
  "ceiling-paint": { set: "plaster", roughness: 0.95, metalness: 0, normalScale: 0.1, detailStrength: 0.1 },
  "ceiling-planks": { set: "ceiling-planks", roughness: 0.85, metalness: 0, normalScale: 0.7, detailStrength: 0.8, directional: true },
  "ceiling-beam": { set: "oak", roughness: 0.85, metalness: 0, normalScale: 0.7, directional: true },
  "fence-wood": { set: "oak", roughness: 0.9, metalness: 0, normalScale: 0.8, directional: true },
  hedge: { set: "grass", roughness: 1, metalness: 0, normalScale: 1.2, tintStrength: 0.85, sheen: { intensity: 0.25, roughness: 0.7 } },
  masonry: { set: "masonry", roughness: 0.95, metalness: 0, normalScale: 0.9, tintStrength: 0 },
  "masonry-painted": { set: "masonry", roughness: 0.9, metalness: 0, normalScale: 0.9, paintOver: true, detailStrength: 0.6 },
  stucco: { set: "stucco", roughness: 0.98, metalness: 0, normalScale: 1, detailStrength: 0.9 },
  "house-siding": { set: "shiplap", roughness: 0.85, metalness: 0, normalScale: 0.9, detailStrength: 0.6 },
  "pergola-wood": { set: "oak", roughness: 0.85, metalness: 0, normalScale: 0.7, directional: true },
  "exterior-paint": { set: "plaster", roughness: 0.95, metalness: 0, normalScale: 0.6, detailStrength: 0.7 },
  "trim-paint": { set: "satin", roughness: 0.4, metalness: 0, normalScale: 0.2, detailStrength: 0.3, clearcoat: { intensity: 0.25, roughness: 0.25 } },
  "door-paint": { set: "painted-wood", roughness: 0.55, metalness: 0, normalScale: 0.35, detailStrength: 0.35, clearcoat: { intensity: 0.2, roughness: 0.3 } },
  // Special
  "glass-clear": { set: null, roughness: 0.02, metalness: 0, normalScale: 0, transmission: 1, ior: 1.52 },
  // The room probe is captured from the middle of the room, so a big glossy
  // panel would mirror the windows in the wrong place; keep it a soft sheen.
  screen: { set: null, roughness: 0.25, metalness: 0, normalScale: 0, clearcoat: { intensity: 0.25, roughness: 0.15 }, envMapIntensity: 0.15 },
  bulb: { set: null, roughness: 0.3, metalness: 0, normalScale: 0 },
  lampshade: { set: "fabric", roughness: 1, metalness: 0, normalScale: 0.5, detailStrength: 0.4 },
  chrome: { set: null, roughness: 0.08, metalness: 1, normalScale: 0 },
  "black-metal": { set: "metal", roughness: 0.55, metalness: 0.35, normalScale: 0.2, detailStrength: 0.3 },
  brass: { set: "metal", roughness: 0.45, metalness: 1, normalScale: 0.3, detailStrength: 0.4 },
  "art-print": { set: null, roughness: 0.8, metalness: 0, normalScale: 0 },
  mirror: { set: null, roughness: 0.015, metalness: 1, normalScale: 0, envMapIntensity: 1.2 },
  soil: { set: "concrete", roughness: 1, metalness: 0, normalScale: 1, detailStrength: 1.4 },
  stem: { set: null, roughness: 0.7, metalness: 0, normalScale: 0 },
  book: { set: null, roughness: 0.65, metalness: 0, normalScale: 0 },
  rubber: { set: null, roughness: 0.9, metalness: 0, normalScale: 0 },
};

/** A procedural canvas texture (art prints). Deterministic per `seed`. */
export interface CanvasTextureSpec {
  kind: "art";
  seed: string;
  palette: string[];
  /** Print aspect (width / height). */
  aspect: number;
}

/** Per-mesh material request. Meshes with equal specs share one material. */
export interface MaterialSpec {
  surface: SurfaceId;
  /** Hex `#rrggbb` tint (sRGB). */
  color: string;
  emissive?: string;
  emissiveIntensity?: number;
  opacity?: number;
  roughness?: number;
  metalness?: number;
  canvas?: CanvasTextureSpec;
  vertexColors?: boolean;
  doubleSided?: boolean;
  /** Emitters the lighting preset dims or brightens (`lamp`, `sconce`, `tv`). */
  glow?: "lamp" | "sconce" | "tv";
  /**
   * Room finish slot (`floor`, `walls`, `wall:outline:2`, `trim`, …). Meshes
   * in a slot share one material whatever its color, so a new color
   * recolors it in place.
   */
  slot?: string;
}

export function materialKey(spec: MaterialSpec): string {
  return JSON.stringify([
    spec.slot ?? null,
    spec.surface,
    spec.slot ? null : spec.color.toLowerCase(),
    spec.emissive ?? null,
    spec.emissiveIntensity ?? null,
    spec.opacity ?? null,
    spec.roughness ?? null,
    spec.metalness ?? null,
    spec.canvas ?? null,
    spec.vertexColors ?? false,
    spec.doubleSided ?? false,
    spec.glow ?? null,
  ]);
}

export function surfaceDef(id: SurfaceId): SurfaceDef {
  return SURFACES[id];
}

/** Texture sets room finishes render with. */
export const FINISH_TEXTURE_SETS: ReadonlySet<TextureSetId> = new Set(
  FINISH_SURFACES.map((id) => SURFACES[id].set).filter((s): s is TextureSetId => s != null)
);
